import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Divider,
  Grid,
  Alert,
  Snackbar,
  Paper,
  Chip,
  Container,
  IconButton,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import PublishIcon from "@mui/icons-material/Publish";
import PreviewIcon from "@mui/icons-material/Preview";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  getBallkidPageConfig,
  saveBallkidPageConfig,
  publishBallkidPageConfig,
} from "../firebaseService";
import { useGroup } from "../context/GroupContext";
import { useTournamentConfig } from "../context/TournamentConfigContext";

const BallkidPageSettings = () => {
  const { activeGroup } = useGroup();
  const { daysOfWeek } = useTournamentConfig();
  const memberSingular = activeGroup?.memberSingular || "Mitglied";
  const groupSlug = activeGroup?.slug || "ballkinder";
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  useEffect(() => {
    const load = async () => {
      const data = await getBallkidPageConfig();
      // Defaults für neue Felder sicherstellen
      setConfig({
        customLinks: [],
        showAssignment: true,
        showFreikarten: false,
        freikartenText: "",
        showImpressum: true,
        impressumUrl: "http://zilker-solutions.de/impressum-datenschutz-ballkid-management",
        ...data,
      });
      setLoading(false);
    };
    load();
  }, []);

  const handleChange = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleCustomLinkChange = (index, field, value) => {
    setConfig((prev) => {
      const links = [...(prev.customLinks || [])];
      links[index] = { ...links[index], [field]: value };
      return { ...prev, customLinks: links };
    });
  };

  const addCustomLink = () => {
    setConfig((prev) => ({
      ...prev,
      customLinks: [...(prev.customLinks || []), { text: "", url: "", enabled: true }],
    }));
  };

  const removeCustomLink = (index) => {
    setConfig((prev) => ({
      ...prev,
      customLinks: (prev.customLinks || []).filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveBallkidPageConfig(config);
      setSnackbar({ open: true, message: "Entwurf gespeichert!", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Fehler beim Speichern", severity: "error" });
    }
    setSaving(false);
  };

  const handlePublish = async () => {
    setSaving(true);
    try {
      await publishBallkidPageConfig({ ...config, daysOfWeek });
      setSnackbar({ open: true, message: "Erfolgreich veröffentlicht!", severity: "success" });
    } catch {
      setSnackbar({ open: true, message: "Fehler beim Veröffentlichen", severity: "error" });
    }
    setSaving(false);
  };

  if (loading || !config) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography color="text.secondary">Config wird geladen...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            {memberSingular}-Seite verwalten
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Konfiguriere die oeffentliche {memberSingular}-Seite (/{groupSlug}/member/:id)
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={() => setShowPreview(!showPreview)}
          >
            {showPreview ? "Editor" : "Vorschau"}
          </Button>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            Speichern
          </Button>
          <Button
            variant="contained"
            startIcon={<PublishIcon />}
            onClick={handlePublish}
            disabled={saving}
            color="success"
          >
            Veröffentlichen
          </Button>
        </Box>
      </Box>

      {showPreview ? (
        <PreviewPanel config={config} />
      ) : (
        <Grid container spacing={2}>
          {/* Slider / Banner */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Scrolling Banner (Slider)</Typography>
                <FormControlLabel
                  control={<Switch checked={config.sliderEnabled} onChange={(e) => handleChange("sliderEnabled", e.target.checked)} />}
                  label="Banner anzeigen"
                />
                {config.sliderEnabled && (
                  <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField label="Banner-Text" value={config.sliderText} onChange={(e) => handleChange("sliderText", e.target.value)} fullWidth multiline />
                    <TextField label="Banner-Link (z.B. mailto: oder https://)" value={config.sliderLink} onChange={(e) => handleChange("sliderLink", e.target.value)} fullWidth />
                    <TextField label="Hintergrundfarbe" value={config.sliderBgColor} onChange={(e) => handleChange("sliderBgColor", e.target.value)} sx={{ width: 200 }} type="color" />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Einteilung */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Einteilung</Typography>
                <FormControlLabel
                  control={<Switch checked={config.showAssignment} onChange={(e) => handleChange("showAssignment", e.target.checked)} />}
                  label="Einteilung (Court/Crew) anzeigen"
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Self Check-In */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Self Check-In</Typography>
                <FormControlLabel
                  control={<Switch checked={config.selfCheckInEnabled} onChange={(e) => handleChange("selfCheckInEnabled", e.target.checked)} />}
                  label="QR-Code Check-In auf der Ballkind-Seite anzeigen"
                />
                {config.selfCheckInEnabled && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Ballkinder koennen auf ihrer Seite einen QR-Code anzeigen, der am Eingang gescannt wird.
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Crew-Zeiten */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Einsatzzeiten</Typography>
                <FormControlLabel
                  control={<Switch checked={config.showCrewTimes} onChange={(e) => handleChange("showCrewTimes", e.target.checked)} />}
                  label="Einsatzzeiten anzeigen"
                />
                <FormControlLabel
                  control={<Switch checked={config.crewTimesLinkEnabled} onChange={(e) => handleChange("crewTimesLinkEnabled", e.target.checked)} />}
                  label="Link zu Crew-Zeiten-Übersicht"
                />
                <TextField label="Turniertag" value={config.crewTimesDay} onChange={(e) => handleChange("crewTimesDay", e.target.value)} fullWidth sx={{ mt: 2 }} helperText='z.B. "19. April"' />
              </CardContent>
            </Card>
          </Grid>

          {/* Treffpunkt */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Treffpunkt-Info</Typography>
                <FormControlLabel
                  control={<Switch checked={config.meetingPointEnabled} onChange={(e) => handleChange("meetingPointEnabled", e.target.checked)} />}
                  label="Treffpunkt anzeigen"
                />
                {config.meetingPointEnabled && (
                  <TextField label="Treffpunkt-Text" value={config.meetingPointText} onChange={(e) => handleChange("meetingPointText", e.target.value)} fullWidth sx={{ mt: 2 }} />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* ATP Link */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Spielplan-Link</Typography>
                <FormControlLabel
                  control={<Switch checked={config.atpLinkEnabled} onChange={(e) => handleChange("atpLinkEnabled", e.target.checked)} />}
                  label="Link anzeigen"
                />
                {config.atpLinkEnabled && (
                  <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField label="Button-Text" value={config.atpLinkText} onChange={(e) => handleChange("atpLinkText", e.target.value)} fullWidth />
                    <TextField label="URL" value={config.atpLinkUrl} onChange={(e) => handleChange("atpLinkUrl", e.target.value)} fullWidth />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Custom Announcement */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Eigene Ankündigung</Typography>
                <FormControlLabel
                  control={<Switch checked={config.customAnnouncementEnabled} onChange={(e) => handleChange("customAnnouncementEnabled", e.target.checked)} />}
                  label="Ankündigung anzeigen"
                />
                {config.customAnnouncementEnabled && (
                  <TextField label="Ankündigungstext" value={config.customAnnouncement} onChange={(e) => handleChange("customAnnouncement", e.target.value)} fullWidth multiline rows={3} sx={{ mt: 2 }} />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Freikarten Info */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Freikarten-Info</Typography>
                <FormControlLabel
                  control={<Switch checked={config.showFreikarten} onChange={(e) => handleChange("showFreikarten", e.target.checked)} />}
                  label="Freikarten-Hinweis anzeigen"
                />
                {config.showFreikarten && (
                  <TextField label="Freikarten-Text" value={config.freikartenText} onChange={(e) => handleChange("freikartenText", e.target.value)} fullWidth multiline rows={2} sx={{ mt: 2 }} placeholder="z.B. Freikarten können am Eingang abgeholt werden." />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Impressum */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2 }}>Impressum & Datenschutz</Typography>
                <FormControlLabel
                  control={<Switch checked={config.showImpressum} onChange={(e) => handleChange("showImpressum", e.target.checked)} />}
                  label="Impressum-Hinweis anzeigen"
                />
                {config.showImpressum && (
                  <TextField label="Impressum-URL" value={config.impressumUrl} onChange={(e) => handleChange("impressumUrl", e.target.value)} fullWidth sx={{ mt: 2 }} />
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Custom Links */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6">Eigene Links</Typography>
                  <Button size="small" startIcon={<AddIcon />} onClick={addCustomLink} disabled={(config.customLinks || []).length >= 3}>
                    Hinzufügen
                  </Button>
                </Box>
                {(config.customLinks || []).map((link, i) => (
                  <Box key={i} sx={{ mb: 2, p: 1.5, bgcolor: "rgba(255,255,255,0.04)", borderRadius: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                      <FormControlLabel
                        control={<Switch checked={link.enabled} onChange={(e) => handleCustomLinkChange(i, "enabled", e.target.checked)} size="small" />}
                        label={`Link ${i + 1}`}
                      />
                      <IconButton size="small" onClick={() => removeCustomLink(i)} color="error"><DeleteIcon fontSize="small" /></IconButton>
                    </Box>
                    <TextField label="Link-Text" value={link.text} onChange={(e) => handleCustomLinkChange(i, "text", e.target.value)} fullWidth size="small" sx={{ mb: 1 }} />
                    <TextField label="URL" value={link.url} onChange={(e) => handleCustomLinkChange(i, "url", e.target.value)} fullWidth size="small" />
                  </Box>
                ))}
                {(config.customLinks || []).length === 0 && (
                  <Typography variant="body2" color="text.secondary">Keine eigenen Links konfiguriert.</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} variant="filled">{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

// Vorschau
const PreviewPanel = ({ config }) => (
  <Paper sx={{ overflow: "hidden", borderRadius: 2, border: "2px solid", borderColor: "primary.main" }}>
    <Box sx={{ bgcolor: "primary.main", px: 2, py: 1 }}>
      <Typography variant="body2" sx={{ color: "white", fontWeight: 600 }}>
        Vorschau — so sieht die Ballkind-Seite aus
      </Typography>
    </Box>
    <Box sx={{ bgcolor: "#191f34", minHeight: 400 }}>
      {config.sliderEnabled && config.sliderText && (
        <Box sx={{ width: "100%", bgcolor: config.sliderBgColor || "#ffca28", color: "#000", fontWeight: "bold", whiteSpace: "nowrap", overflow: "hidden", py: 0.5 }}>
          <Box component={config.sliderLink ? "a" : "span"} href={config.sliderLink || undefined} sx={{ display: "inline-block", px: 2, textDecoration: "none", color: "inherit" }}>
            {config.sliderText}
          </Box>
        </Box>
      )}

      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Typography variant="h5" align="center" color="white" gutterBottom>
          Deine nächste Einteilung ist am [Tag]
        </Typography>

        {config.showAssignment && (
          <Paper elevation={6} sx={{ padding: 3, mt: 3 }}>
            <Typography variant="h6" align="center">Court: [Center Court]</Typography>
            <Typography variant="h6" align="center">Crew: [A]</Typography>
            <Divider sx={{ my: 2 }} />
            {config.showCrewTimes ? (
              <>
                <Typography variant="subtitle1" align="center" sx={{ mb: 2 }}>Deine Einsatzzeiten:</Typography>
                <Box sx={{ display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1 }}>
                  {["11:00", "13:30", "16:00"].map((time) => (
                    <Chip key={time} icon={<AccessTimeIcon style={{ color: "white" }} />} label={time} sx={{ bgcolor: "#1976d2", color: "white" }} />
                  ))}
                </Box>
              </>
            ) : (
              <Typography variant="body2" align="center" color="text.secondary">(Einsatzzeiten ausgeblendet)</Typography>
            )}
          </Paper>
        )}

        {config.customAnnouncementEnabled && config.customAnnouncement && (
          <Paper sx={{ mt: 3, py: 2, px: 2, bgcolor: "rgba(255,255,255,0.12)" }}>
            <Typography variant="body1" align="center" color="white">{config.customAnnouncement}</Typography>
          </Paper>
        )}

        {config.showFreikarten && config.freikartenText && (
          <Paper sx={{ mt: 3, py: 2, px: 2, bgcolor: "rgba(255,255,255,0.10)" }}>
            <Typography variant="body1" align="center" color="white">{config.freikartenText}</Typography>
          </Paper>
        )}

        {config.meetingPointEnabled && (
          <Paper sx={{ mt: 3, py: 2, px: 2, bgcolor: "rgba(255,255,255,0.08)" }}>
            <Typography variant="body1" align="center" color="white">{config.meetingPointText}</Typography>
          </Paper>
        )}

        {config.crewTimesLinkEnabled && (
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Typography sx={{ color: "#90caf9", fontWeight: "bold" }}>Einsatzzeiten aller Crews ansehen</Typography>
          </Box>
        )}

        {(config.customLinks || []).filter((l) => l.enabled && l.text).map((link, i) => (
          <Box key={i} sx={{ textAlign: "center", mt: 2 }}>
            <Typography sx={{ color: "#90caf9", fontWeight: "bold" }}>{link.text}</Typography>
          </Box>
        ))}

        {config.atpLinkEnabled && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Button variant="contained" endIcon={<OpenInNewIcon />} sx={{ background: "linear-gradient(45deg, #2196f3 30%, #21cbf3 90%)", color: "white", fontWeight: "bold", borderRadius: "20px", textTransform: "none" }}>
              {config.atpLinkText}
            </Button>
          </Box>
        )}

        {config.showImpressum && (
          <Typography variant="caption" align="center" color="white" display="block" sx={{ mt: 4 }}>
            Hinweis: Unabhängiges Projekt. <span style={{ color: "#90caf9", textDecoration: "underline" }}>Impressum & Datenschutz</span>
          </Typography>
        )}
      </Container>
    </Box>
  </Paper>
);

export default BallkidPageSettings;
